import React, { useState, useEffect, useRef } from "react";
import { Modal } from "@material-ui/core";
import { isMobile } from "react-device-detect";
import { usePostHog } from "posthog-js/react";
import { useSelector } from "react-redux";
import styles from "./IntervendPromoModal.module.scss";
import { CloseIcon } from "../../../assets";
import {
  shouldShow,
  markShown,
  incrementDismiss,
  markPermanentlyDismissed,
  getDismissCount,
} from "../../../utils/intervendPromoDismissal";

const INTERVEND_URL = "https://web.intervend.rs";
const SWIPE_THRESHOLD_PX = 50;

const slides = [
  {
    key: "revenue-per-device",
    title: "Pratite promete po uređajima",
    description:
      "Jasan pregled prometa po svakom uređaju — real-time statistika, trendovi i poređenja.",
    image: "/images/intervend-promo/revenue-per-device.png",
  },
  {
    key: "sale-search",
    title: "Brza i laka pretraga prodaja",
    description:
      "Pretraga prodaja po datumu, uređaju ili iznosu — trenutan pristup bilo kojoj transakciji.",
    image: "/images/intervend-promo/sale-search.png",
  },
  {
    key: "cardpay-control",
    title: "Kontrola CardPay terminala",
    description:
      "Upravljajte CardPay terminalima na daljinu — provera statusa, storniranje, izdavanje žetona.",
    image: "/images/intervend-promo/cardpay-control.png",
  },
];

const IntervendLogo = ({ className }) => (
  <div className={className}>
    <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none">
      <path
        d="M49.4507 78.5652L22 26H38.1033L45.9464 42.1033C46.6695 43.4939 47.337 44.8845 47.9489 46.2752C48.5607 47.6102 49.1726 49.1398 49.7845 50.8642C50.3963 49.1398 51.0082 47.6102 51.6201 46.2752C52.232 44.8845 52.8995 43.4939 53.6226 42.1033L61.4656 26H76.9015L49.4507 78.5652Z"
        fill="white"
      />
    </svg>
  </div>
);

const IntervendPromoModal = () => {
  const user = useSelector((state) => state.auth.user);
  const userId = user?.id;
  const posthog = usePostHog();

  const [isOpen, setIsOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewedSlides, setViewedSlides] = useState(() => new Set([0]));
  const openedAtRef = useRef(null);
  const autoStartedRef = useRef(false);
  const swipeStartXRef = useRef(null);

  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!userId) return;
    if (!shouldShow(userId)) return;

    autoStartedRef.current = true;
    openModal("auto");
    markShown(userId);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (source) => {
    setIsOpen(true);
    setCurrentSlide(0);
    setViewedSlides(new Set([0]));
    openedAtRef.current = Date.now();
    posthog?.capture("intervend_promo_opened", {
      source,
      device_type: isMobile ? "mobile" : "desktop",
      dismiss_count_before: userId ? getDismissCount(userId) : null,
      timestamp: new Date().toISOString(),
    });
  };

  const closeWithDismiss = (source) => {
    if (userId) incrementDismiss(userId);
    const durationSec = openedAtRef.current
      ? Math.round((Date.now() - openedAtRef.current) / 1000)
      : null;
    posthog?.capture("intervend_promo_dismissed", {
      source,
      duration_seconds: durationSec,
      slides_viewed: viewedSlides.size,
      total_slides: slides.length,
      final_slide: currentSlide,
      dismiss_count_after: userId ? getDismissCount(userId) : null,
    });
    setIsOpen(false);
  };

  const handleDontShowAgain = () => {
    if (userId) markPermanentlyDismissed(userId);
    posthog?.capture("intervend_promo_dont_show_again", {
      slides_viewed: viewedSlides.size,
      final_slide: currentSlide,
    });
    setIsOpen(false);
  };

  const handleOpenIntervend = () => {
    posthog?.capture("intervend_promo_cta_clicked", {
      current_slide: currentSlide,
      slide_key: slides[currentSlide].key,
      slides_viewed: viewedSlides.size,
    });
    window.open(INTERVEND_URL, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  const goToSlide = (index, navType) => {
    const previous = currentSlide;
    setCurrentSlide(index);
    setViewedSlides((seen) => new Set([...seen, index]));
    posthog?.capture("intervend_promo_slide_viewed", {
      from_slide: previous,
      to_slide: index,
      slide_key: slides[index].key,
      navigation_type: navType,
    });
  };

  const prevSlide = () => {
    const next = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
    goToSlide(next, "prev_button");
  };

  const nextSlide = () => {
    const next = currentSlide === slides.length - 1 ? 0 : currentSlide + 1;
    goToSlide(next, "next_button");
  };

  const handlePointerDown = (e) => {
    swipeStartXRef.current = e.clientX;
  };

  const handlePointerUp = (e) => {
    if (swipeStartXRef.current == null) return;
    const delta = e.clientX - swipeStartXRef.current;
    swipeStartXRef.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    if (delta < 0) {
      const next =
        currentSlide === slides.length - 1 ? 0 : currentSlide + 1;
      goToSlide(next, "swipe_left");
    } else {
      const next =
        currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
      goToSlide(next, "swipe_right");
    }
  };

  const handlePointerCancel = () => {
    swipeStartXRef.current = null;
  };

  return (
    <>
      <button
        type="button"
        className={styles.bannerTile}
        onClick={() => openModal("banner_click")}
      >
        <IntervendLogo className={styles.bannerLogo} />
        <div className={styles.bannerText}>
          <div className={styles.bannerTitle}>
            Praćenje prometa i statusa mašina
          </div>
          <div className={styles.bannerSubtitle}>
            Novi Intervend interfejs · web.intervend.rs
          </div>
        </div>
        <div className={styles.bannerCta}>
          Pogledaj <span className={styles.bannerArrow}>→</span>
        </div>
      </button>

      <Modal open={isOpen} onClose={() => closeWithDismiss("backdrop_or_esc")}>
        <div className={styles.modalWrap}>
          <div className={styles.modalInner}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <IntervendLogo className={styles.modalLogo} />
                <h2 className={styles.modalTitle}>
                  Intervend — novi panel za praćenje prometa
                  <span className={styles.modalSubtitle}>
                    Ulogujte se sa postojećim SDF nalogom na web.intervend.rs
                  </span>
                </h2>
              </div>
              <div
                className={styles.closeIconWrap}
                onClick={() => closeWithDismiss("close_button")}
                role="button"
                aria-label="Zatvori"
              >
                <img src={CloseIcon} alt="Zatvori" className={styles.closeIcon} />
              </div>
            </div>

            <div className={styles.modalBody}>
              <div
                className={styles.viewport}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onPointerLeave={handlePointerCancel}
              >
                <div
                  className={styles.track}
                  style={{
                    transform: `translate3d(-${currentSlide * 100}%, 0, 0)`,
                  }}
                >
                  {slides.map((s) => (
                    <div key={s.key} className={styles.slide}>
                      <div className={styles.phoneFrame}>
                        <div className={styles.phoneNotch} />
                        <img
                          src={s.image}
                          alt={s.title}
                          className={styles.slideImage}
                          draggable={false}
                        />
                      </div>
                      <div className={styles.slideCopy}>
                        <h3 className={styles.slideTitle}>{s.title}</h3>
                        <p className={styles.slideDescription}>
                          {s.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.navRow}>
                <button
                  type="button"
                  className={styles.navArrow}
                  onClick={prevSlide}
                  aria-label="Prethodno"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <div className={styles.dots}>
                  {slides.map((s, index) => (
                    <button
                      key={s.key}
                      type="button"
                      className={`${styles.dot} ${
                        index === currentSlide ? styles.dotActive : ""
                      }`}
                      onClick={() => goToSlide(index, "dot")}
                      aria-label={`Slajd ${index + 1}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.navArrow}
                  onClick={nextSlide}
                  aria-label="Sledeće"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.ctaButton}
                onClick={handleOpenIntervend}
              >
                Otvori web.intervend.rs
                <span className={styles.ctaArrow} aria-hidden="true">
                  →
                </span>
              </button>
              <button
                type="button"
                className={styles.dontShowAgain}
                onClick={handleDontShowAgain}
              >
                Ne prikazuj više
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default IntervendPromoModal;
